/**
 * An album's credits the way liner notes print them (Gordon 2026-11-03:
 * "all the tracks and credits should be listed here … like a real album credit
 * listing all assembled here as well as individual tracks"). Pure: the release
 * page gives it the release's own credits and each track's credits.
 *
 *   assembleAlbumCredits → one line per role, each person with the tracks they
 *   did it on ("Producer: Adam Baptiste (1–6, 8)"); a release-level credit, or
 *   one on every track, has no track list — it covers the whole album.
 */

import { nameKey } from './copilot-credits';

export interface SheetCredit { role: string; name: string; href?: string }
export interface SheetTrack { n: number; title: string; href: string; credits: SheetCredit[] }
export interface SheetPerson { name: string; href?: string; tracks: number[] }
export interface SheetLine { role: string; people: SheetPerson[] }

// Liner-notes order; anything else follows alphabetically.
const ROLE_ORDER = [
  'primary artist', 'artist', 'featured artist', 'featuring', 'producer', 'co-producer', 'executive producer',
  'songwriter', 'writer', 'composer', 'lyricist', 'arranger', 'vocalist', 'vocals', 'rapper', 'mc', 'backing vocals',
  'dj', 'scratches', 'musician', 'performer', 'instrumentalist', 'guitar', 'bass', 'drums', 'keyboards', 'piano',
  'programming', 'remixer', 'recording engineer', 'engineer', 'mixing engineer', 'mixed by', 'mastering engineer',
  'mastered by', 'recorded at', 'mixed at', 'mastered at', 'artwork', 'design', 'photography', 'photographer',
  'label', 'publisher',
];
const roleRank = (r: string) => {
  const i = ROLE_ORDER.indexOf(r.trim().toLowerCase());
  return i === -1 ? ROLE_ORDER.length : i;
};
const personKey = (c: { name: string; href?: string }) => (c.href && c.href.startsWith('/profile/') ? c.href : `n:${nameKey(c.name)}`);
const roleKey = (r: string) => r.trim().toLowerCase();

/** "1–3, 5, 7–8" */
export function trackRanges(nums: number[]): string {
  const s = [...new Set(nums)].filter(n => Number.isFinite(n)).sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < s.length; i++) {
    let j = i;
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j++;
    out.push(j > i ? `${s[i]}–${s[j]}` : String(s[i]));
    i = j;
  }
  return out.join(', ');
}

/** "02 Yes, yes, Y_all" on track 2 reads "Yes, yes, Y_all" next to its number. */
export function cleanTrackTitle(title: string, n: number): string {
  const m = String(title || '').match(/^\s*0*(\d+)[\s.\-_)]+(.+)$/);
  return m && Number(m[1]) === n ? m[2].trim() : String(title || '').trim();
}

/** One track's credits grouped by role, in liner-notes order, each person once. */
export function byRole(credits: SheetCredit[]): SheetLine[] {
  const lines = new Map<string, SheetLine>();
  for (const c of credits) {
    if (!c?.name?.trim() || !c.role?.trim()) continue;
    const k = roleKey(c.role);
    const line = lines.get(k) || { role: c.role.trim(), people: [] };
    if (!line.people.some(p => personKey(p) === personKey(c))) line.people.push({ name: c.name.trim(), href: c.href, tracks: [] });
    lines.set(k, line);
  }
  return [...lines.values()].sort((a, b) => roleRank(a.role) - roleRank(b.role) || a.role.localeCompare(b.role));
}

export function assembleAlbumCredits(album: SheetCredit[], tracks: SheetTrack[]): SheetLine[] {
  const lines = new Map<string, { role: string; people: Map<string, SheetPerson & { whole: boolean }> }>();
  const add = (c: SheetCredit, track: number | null) => {
    if (!c?.name?.trim() || !c.role?.trim()) return;
    const k = roleKey(c.role);
    const line = lines.get(k) || { role: c.role.trim(), people: new Map() };
    const pk = personKey(c);
    const p = line.people.get(pk) || { name: c.name.trim(), href: c.href, tracks: [], whole: false };
    if (!p.href && c.href) p.href = c.href;
    if (track === null) p.whole = true;
    else if (!p.tracks.includes(track)) p.tracks.push(track);
    line.people.set(pk, p);
    lines.set(k, line);
  };
  for (const c of album) add(c, null);
  for (const t of tracks) for (const c of t.credits) add(c, t.n);
  const all = new Set(tracks.map(t => t.n));
  return [...lines.values()]
    .map(l => ({
      role: l.role,
      people: [...l.people.values()]
        .map(({ whole, ...p }) => ({ ...p, tracks: whole || (all.size > 1 && p.tracks.length >= all.size) ? [] : p.tracks.sort((a, b) => a - b) }))
        // Whole-album credits first, then most tracks; otherwise the order they're
        // credited in (Gordon 2026-11-03: "always Ronny's name before mine when mixing").
        .sort((a, b) => (a.tracks.length === 0 ? -1 : 0) - (b.tracks.length === 0 ? -1 : 0) || b.tracks.length - a.tracks.length),
    }))
    .sort((a, b) => roleRank(a.role) - roleRank(b.role) || a.role.localeCompare(b.role));
}
